import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
  Typography
} from '@mui/material';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { PartnerCompanyEntity } from 'src/entities/PartnerCompanyEntity';
import { SalesOrderCandidatePartnerCompany } from 'src/entities/SalesOrder.response.entity';
import { useMutationPartnerCompanies } from 'src/hooks/usePartnerCompanies.request';

type Props = {
  selectedPartners: SalesOrderCandidatePartnerCompany[];
  partners: PartnerCompanyEntity[];
  onConfirm: (partners: SalesOrderCandidatePartnerCompany[]) => void;
  dialogOpen?: boolean;
  onClose?: () => void;
  renderTrigger?: (handleOpen: (event: React.MouseEvent<HTMLElement>) => void) => React.ReactNode;
};

function CandidateCompanySelector({
  selectedPartners,
  partners,
  onConfirm,
  dialogOpen: externalDialogOpen,
  onClose: externalOnClose,
  renderTrigger
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [internalSelectedPartners, setInternalSelectedPartners] = useState<SalesOrderCandidatePartnerCompany[]>([]);

  useEffect(() => {
    setInternalSelectedPartners(selectedPartners);
  }, [selectedPartners]);

  const { addPartnerCompany } = useMutationPartnerCompanies();

  const dialogOpen = externalDialogOpen ?? internalDialogOpen;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handlePartnerSelect = (partnerId: number) => {
    setInternalSelectedPartners((prev) => {
      const ids = prev.map((it) => it.partnerCompanyId);
      if (ids.includes(partnerId)) {
        return prev.filter((it) => it.partnerCompanyId !== partnerId);
      }
      const newPartner = partners.find((it) => it.id === partnerId);
      return [...prev, { partnerCompanyId: newPartner.id, name: newPartner.name }];
    });
  };

  const handleCreatePartner = async () => {
    if (!searchQuery) return;
    await addPartnerCompany.mutateAsync({
      name: searchQuery,
    }, {
      onSuccess: (response: AxiosResponse<PartnerCompanyEntity>) => {
        setInternalSelectedPartners((prev) => ([
          ...prev,
          { partnerCompanyId: response.data.id, name: response.data.name }
        ]));
      }
    });
  };

  const handleConfirm = () => {
    onConfirm(internalSelectedPartners);
    handleClose();
  };

  const filteredPartners = partners?.filter((it) => it.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setInternalDialogOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalDialogOpen(false);
    }
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleOpen)
      ) : (
        <>
          <Button
            variant="contained"
            size="small"
            onClick={handleOpen}
          >
            依頼候補企業を選択
          </Button>
          <Typography variant="body2">
            {selectedPartners.map((partner) => partner.name).join(', ')}
          </Typography>
        </>
      )}

      <Popover
        open={dialogOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ width: 400, p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="検索"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <FormGroup sx={{ mt: 2 }}>
            {filteredPartners?.map((partner) => (
              <FormControlLabel
                key={['partnerSelection', partner.id].join('-')}
                control={(
                  <Checkbox
                    checked={internalSelectedPartners.map((it) => it.partnerCompanyId).includes(partner.id)}
                    onChange={() => handlePartnerSelect(partner.id)}
                  />
                )}
                label={partner.name}
              />
            ))}
          </FormGroup>
          {searchQuery && (
            <>
              <Divider sx={{ my: 1 }} />
              {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
              <MenuItem onClick={handleCreatePartner}>
                <ListItemText primary={`"${searchQuery}" を新規作成`} />
              </MenuItem>
            </>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <Button onClick={handleClose}>
              キャンセル
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
            >
              確定
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export default CandidateCompanySelector;
